import { PinSolidIcon } from '@ikyucom/design-system-icons-react'
import { type FC, useEffect, useId } from 'react'
import { useEnv } from '~/env'
import { RestaurantCoordinate } from '~/features/guide/restaurantCoordinate.frag'
import { getFragmentData } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import { useIsPC } from '~/lib/ua'
import { css, cx } from '~/styled-system/css'

type Props = {
  fragment: FragmentType<typeof RestaurantCoordinate>
  className?: string
}

export const Map: FC<Props> = import.meta.env.VITE_MODE === 'ppg' ? MapBoxMap : GoogleMap

// 意図的にiframeを使用している
// google mapのAPIを使用すると金額がシャレにならない。google map APIダメ。絶対。
// @see: https://ikyu.slack.com/archives/C0F6QL963/p1698802792050969
function GoogleMap({ className, fragment }: Props) {
  const coordinate = getFragmentData(RestaurantCoordinate, fragment)
  const lat = Number(coordinate.latitude)
  const lng = Number(coordinate.longitude)
  const zoom = coordinate.zoom
  return (
    <div className={className}>
      <iframe
        className={css({ w: 'full', h: 'full', _pc: { h: '[450px]' } })}
        src={`https://www.google.com/maps/embed/v1/place?q=${lat},${lng}&zoom=${zoom}&key=AIzaSyA8SAVf92d-A6ZU_XhgvzgMCFHaU6fzO1E`}
      ></iframe>
    </div>
  )
}

function MapBoxMap({ className, fragment }: Props) {
  const { MAPBOX_ACCESS_TOKEN } = useEnv()
  const id = useId()

  const isPc = useIsPC()

  const coordinate = getFragmentData(RestaurantCoordinate, fragment)
  const lat = Number(coordinate.latitude)
  const lng = Number(coordinate.longitude)

  // MapBox の zoom level は Google Map から -1 でだいたい同じ縮尺
  // https://github.com/ikyucom/macaron-frontend/issues/5544
  const zoom = coordinate.zoom - 1

  useEffect(() => {
    if (!MAPBOX_ACCESS_TOKEN) {
      return
    }
    // dynamic import で chunk 分割
    // これにより一休モードでは mapbox-gl がロードされることはない
    // https://github.com/ikyucom/macaron-frontend/pull/5335#issuecomment-2576548899
    import('mapbox-gl').then((mapboxgl) => {
      // @ts-ignore dynamic import だと accessToken が参照できないで ts-ignore で回避
      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN
      const map = new mapboxgl.Map({
        container: id,
        style: 'mapbox://styles/yahoojapan/ck353yf380a0k1cmcdx7jc1xq',
        center: [lng, lat],
        zoom, // 地図の初期ズームレベル
        attributionControl: false,
      })
      new mapboxgl.Marker().setLngLat([lng, lat]).addTo(map)
      map.addControl(new mapboxgl.ScaleControl(), 'bottom-left')

      map.on('load', function () {
        const br = map.getContainer().querySelector('.mapboxgl-ctrl-bottom-right')
        if (br === null) {
          return
        }
        br.innerHTML = `
         <div class="mapboxgl-ctrl mapboxgl-ctrl-attrib">
           <div class="mapboxgl-ctrl-attrib-inner">
           <a href="https://www.mapbox.com/about/maps/" target="_blank">(C) Mapbox</a>
           <a href="https://www.openstreetmap.org/about/" target="_blank">(C) OpenStreetMap</a>
           <a href="https://map.yahoo.co.jp/promo/app/license/attribution.html" target="_blank">(C) LY Corporation</a>
           </div>
         </div>`
        // Yahoo! トラベルにあわせて SD では拡大・縮小は表示しない
        if (isPc) {
          // copyright を設定後に追加しないとコントロールが動かない
          map.addControl(
            // オプションは Yahoo! トラベル準拠
            new mapboxgl.NavigationControl({
              showCompass: false,
              showZoom: true,
              visualizePitch: false,
            }),
            'bottom-right'
          )
        }
      })
    })
  }, [MAPBOX_ACCESS_TOKEN, id, lat, lng, zoom, isPc])

  if (!MAPBOX_ACCESS_TOKEN) {
    return null
  }

  const link = new URL(`https://map.yahoo.co.jp/place`)
  link.searchParams.set('lat', lat.toString())
  link.searchParams.set('lon', lng.toString())
  link.searchParams.set('zoom', zoom.toString())

  return (
    <div className={cx(css({ position: 'relative' }), className)}>
      <div id={id} className={css({ w: 'full', h: 'full', _pc: { h: '[450px]' } })}></div>
      <a
        href={link.toString()}
        target="_blank"
        rel="noopener"
        className={css({
          position: 'absolute',
          right: '0',
          top: '0',
          mr: '2',
          mt: '2',
          display: 'flex',
          alignItems: 'center',
          rounded: 'lg',
          border: 'light',
          bg: 'white',
          p: '2',
          color: 'link',
        })}
      >
        <PinSolidIcon
          width="16"
          height="16"
          className={css({ display: 'inline-block', fill: 'link' })}
        />
        <span className={css({ ml: '1' })}>地図をみる</span>
      </a>
    </div>
  )
}
